import BrowseCategoryShared from "../../../components/browse-category-shared";
import {
	Cats,
	getCategoryGroupById,
	getCategoryGroupBySlug,
	getSubcategoryBySlug,
	getSubcategoriesByGroupId,
} from '../../../generated/static-categories';

export async function getStaticPaths() {
	// Only prerender category groups, not subcategories
	const paths = Object.values(Cats)
		.filter(cat => cat.slug) // Exclude "All" which has empty slug
		.map(cat => ({
			params: { category: cat.slug }
		}));

	return {
		paths,
		fallback: 'blocking' // Generate subcategories and new categories on-demand
	};
}

export async function getStaticProps(context) {
	const {
		params: { category },
	} = context;

	// Use static category data instead of database queries
	// First try to find as a subcategory
	const subcategoryData = getSubcategoryBySlug(category);

	let categoryGroupId = 0;
	let catName = '';
	let isSubcategory = false;

	if (subcategoryData) {
		// It's a subcategory
		categoryGroupId = subcategoryData.id;
		catName = subcategoryData.label;
		isSubcategory = true;
	} else {
		// Try to find as a category group
		const categoryGroupData = getCategoryGroupBySlug(category);
		// An unknown or retired slug is a 404. It used to render as group 0, an
		// empty page with an empty title, indexable forever.
		if (!categoryGroupData) return { notFound: true, revalidate: 3600 };
		categoryGroupId = categoryGroupData.id;
		catName = categoryGroupData.label;
	}

	if (
		typeof process != "undefined" &&
		(process.env.BUILD_FOR_CAPACITOR || process.env.BUILD_FOR_CAPACITOR_STG)
	) {
		return {
			props: {
				mobileTopNav: "left",
				currentList: [],
				interceptBack: true,
				currentCategoryGroupId: categoryGroupId,
				currentCategoryId: isSubcategory ? categoryGroupId : null,
				categorySlug: category,
				isSubcategory,
				catName: catName,
			},
		};
	}

	// No listings come from the server: the grid and the showcase rows are both
	// fetched in the browser (useBrowseListings, SearchListing). A server list
	// is one ISR copy per category, unfiltered and unsorted, and every filtered
	// or sorted visit had to throw it away. Back from a listing was worse: the
	// page kept the cached unfiltered list and skipped the client fetch, so
	// /browse/fashion?deals=true came back as plain Fashion (owner, 2026-10-12).
	//
	// The page still sends the subcategory order, so the client knows which
	// showcase rows to fetch first. Most looked-at subcategories first; those
	// under the coverage floor go last, a thin row should not lead the page
	// (utils/category-coverage.ts).
	let showcaseData = null;
	if (categoryGroupId && !isSubcategory) {
		const { orderSubcategoriesForBrowse } = await import("../../../utils/category-coverage");
		const subcategories = getSubcategoriesByGroupId(categoryGroupId);
		const allSubcategoryIds = await orderSubcategoriesForBrowse(subcategories.map(sc => sc.id));
		if (allSubcategoryIds.length > 0) {
			showcaseData = {
				fullShowcases: [],
				partialShowcases: [],
				hasEnoughForShowcase: false,
				allSubcategoryIds,
				categoryGroupId,
			};
		}
	}

	// Get category group label for the "More" showcase
	const categoryGroup = getCategoryGroupById(categoryGroupId);
	const categoryGroupLabel = categoryGroup?.label || catName;

	return {
		props: {
			mobileTopNav: "left",
			currentList: [],
			interceptBack: true,
			currentCategoryGroupId: categoryGroupId,
			catName: catName,
			categorySlug: category,
			isSubcategory: isSubcategory,
			showcaseData: showcaseData,
			categoryGroupLabel: categoryGroupLabel,
		},
		revalidate: 3600, // ISR: Revalidate every 1 hour
	};
}

export default BrowseCategoryShared;
